/** Governs function of slate formatting buttons (underline, bold, etc.), used by EditorToolbar */
import React, { useEffect } from "react";
import IconButton from "@mui/material/Button";
import { Editor, Transforms, Element as SlateElement } from "slate";
import { useSlate } from "slate-react";
import { EditorButtonTypes } from "./types";
import {
    unWrapLink,
    isLinkActive,
    insertLink,
    insertImage,
    isImageUrl,
} from "./utils";
import { Tooltip } from "@js/components/common/datadisplay/Tooltip";
import { WINDOWS, MACOS, MAC } from "../../constants/common";

declare global {
    interface Navigator {
        userAgentData: any;
    }
}

/**
 * Adds the formatting passed to the "marked" section
 * @param {ReactEditor} editor Selected text
 * @param {string} formatting Assign formatting to selection
 */
const toggleMark = (editor: any, formatting: string): void => {
    const marks = Editor.marks(editor);
    const isActive = marks ? marks[formatting] === true : false;

    if (isActive) Editor.removeMark(editor, formatting);
    else Editor.addMark(editor, formatting, true);
};

/**
 * Helper function for toggleBlock, checks if a section is already blocked
 * @param {ReactEditor} editor Selected text
 * @param {string} formatting If there is already block format, checks if it is the same as formatting
 * @returns {boolean} Truth value of if selection already has a block format (bullet-list, header, etc)
 */
const isBlockActive = (editor: any, formatting: string): boolean => {
    const [match] = Editor.nodes(editor, {
        match: (n) =>
            !Editor.isEditor(n) &&
            SlateElement.isElement(n) &&
            n.type === formatting,
    });

    return !!match;
};

/**
 * Block formatting is exclusive for each line, and will overwrite older block formats
 * @param {ReactEditor} editor Selected text
 * @param {string} formatting Assign formatting to selection
 */
const toggleBlock = (editor: any, formatting: string): void => {
    const LIST_TYPES = ["numbered-list", "bulleted-list"];
    const isActive = isBlockActive(editor, formatting);
    const isList = LIST_TYPES.includes(formatting);

    Transforms.unwrapNodes(editor, {
        match: (n) =>
            LIST_TYPES.includes(
                // @ts-ignore
                !Editor.isEditor(n) && SlateElement.isElement(n) && n.type
            ),
        split: true,
    });
    const newProperties: Partial<SlateElement> = {
        type: isActive ? "paragraph" : isList ? "list-item" : formatting,
    };
    Transforms.setNodes(editor, newProperties);

    if (!isActive && isList) {
        const block = { type: formatting, children: [] };
        Transforms.wrapNodes(editor, block);
    }
};

/**
 * Changes text color
 * @param {ReactEditor} editor Selected text
 * @param {string} color Color to change selection to
 */
const changeColor = (editor: any, color: string): void => {
    const marks = Editor.marks(editor);

    if (marks !== null && marks["color"] === color)
        Editor.addMark(editor, "color", "grey");
    else Editor.addMark(editor, "color", color);
};

/**
 * Creates custom button that will apply its passed formatting when clicked
 * @param {string} formatting HTML name for formatting the button should apply
 * @param {string} formatType Type of format (mark, block, or color)
 * @param {svg icon} formatIcon Icon for button to display
 * @returns {jsx} Returns all slate functionality as a iconButton
 */
const EditorButton = ({
    formatting,
    formatType,
    formatIcon,
}: EditorButtonTypes) => {
    const editor = useSlate();
    let platform: string = "";

    // define platform in an effect because it does not exist when rendered on the server
    useEffect(() => {
        platform =
            navigator?.userAgentData?.platform || navigator?.platform || "";
    }, []);

    /**
     * @param formatting name of the formatting
     * @returns macOS hotkeys tips
     */
    const renderMACOSTooltip = (formatting: string) => {
        return MACOS[formatting] ? MACOS[formatting] : "";
    };

    /**
     * @param formatting name of the formatting
     * @returns windows hotkeys tips
     */
    const renderWINDOWSTooltip = (formatting: string) => {
        return WINDOWS[formatting] ? WINDOWS[formatting] : "";
    };

    function isValidUrl(url: string): boolean {
        if (!url) return false;

        // make sure it contains all required characters
        const requiredChars = ["."];
        const containsAllRequiredChars =
            requiredChars.filter((char) => url.indexOf(char) !== -1).length ==
            requiredChars.length;
        if (!containsAllRequiredChars) return false;

        // make sure it does not contain any invalid characters (spaces, tabs, newlines, etc)
        const invalidChars = [" ", "\t", "\n"];
        const containsInvalidChar =
            invalidChars.filter((char) => url.indexOf(char) !== -1).length > 0;
        if (containsInvalidChar) return false;

        return true;
    }

    return (
        <Tooltip
            title={
                platform.toLowerCase().indexOf(MAC) < 0
                    ? renderMACOSTooltip(formatting)
                    : renderWINDOWSTooltip(formatting)
            }
        >
            <IconButton
                className={formatting}
                sx={{
                    color: (theme) => theme.palette.common.black,
                    p: 1.5,
                }}
                size="small"
                onClick={(event) => {
                    event.preventDefault();
                    //The formatType "mark" only affects the selected section, "block" will affect the whole line
                    if (formatType === "mark") toggleMark(editor, formatting);
                    else if (formatType === "block")
                        toggleBlock(editor, formatting);
                    else if (formatType === "color")
                        changeColor(editor, formatting);
                    else if (formatType === "link") {
                        if (isLinkActive(editor)) unWrapLink(editor);
                        else {
                            const url = window.prompt(
                                "Enter the URL of the link:"
                            );
                            if (!url) return;
                            if (!isValidUrl(url)) {
                                alert("Invalid URL");
                                return;
                            }
                            insertLink(editor, url);
                        }
                    } else if (formatType === "image") {
                        const url = window.prompt(
                            "Enter the URL of the image:"
                        );
                        if (!url) {
                            return;
                        } else if (!isImageUrl(url)) {
                            alert("URL is not an image");
                            return;
                        } else {
                            insertImage(editor, url);
                        }
                    }
                }}
            >
                {formatIcon}
            </IconButton>
        </Tooltip>
    );
};

export default EditorButton;
